/** The person's latest bot tasks in QuantisAI's Bots view, so a job started
 * with "@Bot do this" can be followed and its changes approved. Hidden until
 * there is a task unless `emptyText` is given. */
import { useState } from 'react';
import { Button } from '../../shared/ui/button';
import { BotTaskCard } from '../workspaces/bots/BotTaskCard';
import { useBotTasks } from '../workspaces/bots/useBotTasks';
import { isBotTaskActive, type BotTask } from '../../services/BotTasksService';

const SHOWN = 5;

/** Exported for tests: the tasks to show, working or waiting ones first, so
 * one that still needs an approval or a Stop is never pushed out of sight by
 * newer finished ones. Within each group the newest come first. */
export function shownTasks(tasks: BotTask[], limit = SHOWN): BotTask[] {
  return [...tasks.filter(isBotTaskActive), ...tasks.filter((task) => !isBotTaskActive(task))].slice(0, limit);
}

/** `refreshKey` changes when a QuantisAI request starts or settles, failed
 * or stopped ones included: an "@Bot do this" request may have started a
 * task even when the reply never arrived. */
export function SIBotTasks({ workspaceId, refreshKey, limit, emptyText }: { workspaceId: string; refreshKey: string | number; limit?: number; emptyText?: string }) {
  const [retries, setRetries] = useState(0);
  const { tasks, loaded, error, upsert } = useBotTasks(workspaceId, undefined, `${refreshKey}:${retries}`);
  if (!tasks.length && !error && !(emptyText && loaded)) return null;
  return (
    <section aria-label="Bot tasks" className="mb-4 space-y-2">
      <p className="text-xs font-medium text-muted-foreground">Bot tasks</p>
      {error && (
        <div role="alert" className="flex items-center gap-2 text-xs text-destructive">
          <span className="min-w-0 flex-1">Bot tasks could not be loaded. {error}</span>
          <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => setRetries((n) => n + 1)}>Retry</Button>
        </div>
      )}
      {!tasks.length && !error && <p className="text-xs text-muted-foreground">{emptyText}</p>}
      {shownTasks(tasks, limit).map((task) => <BotTaskCard key={task.id} task={task} onChange={upsert} compact />)}
    </section>
  );
}
